/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';

import {
  AccessTokenPairType,
  RefreshTokenPairType,
  TokenPairType,
} from '@api/types';
import { LOCAL_STORAGE_KEYS } from '@constants/local-storage-constants';

export type AuthState = {
  access: string | null;
  refresh: string | null;
  accessExpiresAt: number;
  refreshExpiresAt: number;
};

const getTokenExpiresAt = (expiresIn: number): number => {
  if (!expiresIn) return 0;
  const now = Date.now();
  return now + expiresIn * 1000;
};

const isTokenExpired = ({
  token,
  expiresAt,
}: {
  token: string | null,
  expiresAt: number,
}): boolean => {
  if (!token || !expiresAt) return true;
  return Date.now() >= expiresAt;
};

const initialState = (): AuthState => {
  let item: string | null = null;

  const access = (
    localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN)
    || localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN_DEPRECATED)
  );
  const refresh = (
    localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN)
    || localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN_DEPRECATED)
  );

  item = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_EXPIRES_AT);
  const accessExpiresAt: number = item === null ? 0 : parseInt(item, 10);

  item = localStorage.getItem(LOCAL_STORAGE_KEYS.REFRESH_EXPIRES_AT);
  const refreshExpiresAt: number = item === null ? 0 : parseInt(item, 10);

  return {
    access,
    refresh,
    accessExpiresAt,
    refreshExpiresAt,
  };
};

export const useAuthStore = defineStore({
  id: 'auth',
  state: initialState,
  getters: {
    authorized: (state) => !isTokenExpired({
      token: state.refresh,
      expiresAt: state.refreshExpiresAt,
    }),
    accessExpired: (state) => isTokenExpired({
      token: state.access,
      expiresAt: state.accessExpiresAt,
    }),
    refreshExpired: (state) => isTokenExpired({
      token: state.refresh,
      expiresAt: state.refreshExpiresAt,
    }),
  },
  actions: {
    $reset() {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN_DEPRECATED);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN_DEPRECATED);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_EXPIRES_AT);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.REFRESH_EXPIRES_AT);
      Object.assign(this, initialState());
    },
    setAccessToken(tokenPair: AccessTokenPairType) {
      this.access = tokenPair.access || null;
      this.accessExpiresAt = getTokenExpiresAt(tokenPair.accessExpiresIn);

      if (this.access === null) throw Error('Invalid access token.');
      if (this.accessExpiresAt === null) throw Error('Invalid accessExpiresAt');

      localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, this.access);
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.ACCESS_EXPIRES_AT,
        this.accessExpiresAt.toString(),
      );
    },
    setRefreshToken(tokenPair: RefreshTokenPairType) {
      this.refresh = tokenPair.refresh || null;
      this.refreshExpiresAt = getTokenExpiresAt(tokenPair.refreshExpiresIn);

      if (this.refresh === null) throw Error('Invalid refresh token.');
      if (this.refreshExpiresAt === null) throw Error('Invalid refreshExpiresAt');

      localStorage.setItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, this.refresh);
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.REFRESH_EXPIRES_AT,
        this.refreshExpiresAt.toString(),
      );
    },
    setTokenPair(tokenPair: TokenPairType) {
      this.setAccessToken(tokenPair);
      this.setRefreshToken(tokenPair);
    },
  },
});
